import { useState, Fragment } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Dialog, Transition, Menu } from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  MapIcon,
  PaperAirplaneIcon,
  ChartBarIcon,
  DocumentChartBarIcon,
  ShieldCheckIcon,
  CpuChipIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useAuthStore } from '../../store/authStore';
import { UserRole } from '@airrecon/shared';
import NotificationDropdown from './NotificationDropdown';
import Breadcrumbs from './Breadcrumbs';
import SystemAlertBanner from './SystemAlertBanner';
import { useRealtimeEvents } from '../../hooks/useRealtimeEvents';

const monitoringNav = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Live Map', href: '/map', icon: MapIcon },
  { name: 'Analytics', href: '/analytics', icon: ChartBarIcon },
  { name: 'Reports', href: '/reports', icon: DocumentChartBarIcon },
];

// Missions is hidden by default. Set VITE_MISSIONS_ENABLED=true to surface
// it again — the page itself is still wired in App.tsx behind the same flag.
// We hide rather than delete because the underlying schema/routes are still
// referenced by the alerts pipeline and by tests.
const missionsEnabled = import.meta.env.VITE_MISSIONS_ENABLED === 'true';

const managementNav = [
  ...(missionsEnabled
    ? [{ name: 'Missions', href: '/missions', icon: PaperAirplaneIcon }]
    : []),
  { name: 'Geofences', href: '/geofences', icon: ShieldCheckIcon },
  { name: 'Sensors', href: '/sensors', icon: CpuChipIcon },
];

const adminNav = [
  { name: 'Admin', href: '/admin', icon: UserGroupIcon },
];

// Brand lockup for the sidebar header. We use a horizontal arrangement
// of the brand mark (shield) + wordmark assets rather than the stacked
// logo-on-dark.png — the stacked version is square (1254×1254) and at
// any reasonable height it leaves ~80% of the sidebar width unused.
// The horizontal split fills the available space naturally and reads
// at a glance. Signal Red 'C2' badge tags the platform.
function BrandLockup() {
  return (
    <div className="flex items-center gap-2.5">
      <img
        src="/brand/logo-mark.png"
        alt=""
        aria-hidden="true"
        className="h-12 w-auto"
      />
      <img
        src="/brand/logo-wordmark.png"
        alt="Airspace Surveillance of New England"
        className="h-7 w-auto"
        // Brand kit forbids stretching or recoloring; width: auto keeps
        // the aspect ratio off the explicit height.
      />
      <span className="inline-flex items-center rounded-sm bg-brand-red px-1.5 py-0.5 font-brand text-[10px] font-bold tracking-[0.18em] text-white">
        C2
      </span>
    </div>
  );
}

function UserInitials({ firstName, lastName }: { firstName?: string; lastName?: string }) {
  const initials = `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase() || '?';
  return (
    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-accent/20 text-accent text-xs font-bold">
      {initials}
    </div>
  );
}

function SidebarContent({ onNavClick }: { onNavClick?: () => void }) {
  const location = useLocation();
  const { user } = useAuthStore();
  const isAdmin = user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN;

  const renderNavItems = (items: typeof monitoringNav) =>
    items.map((item) => {
      const isActive = location.pathname === item.href ||
        (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
      return (
        <li key={item.name}>
          <Link
            to={item.href}
            onClick={onNavClick}
            className={clsx(
              'group flex items-center gap-x-3 rounded-sm px-3 py-2 text-[13px] font-medium transition-colors duration-150',
              isActive
                ? 'bg-accent/10 text-accent nav-active'
                : 'text-iron-300 hover:text-iron-100 hover:bg-iron-700'
            )}
          >
            <item.icon className={clsx('h-4 w-4 flex-shrink-0', isActive ? 'text-accent' : 'text-iron-400 group-hover:text-iron-200')} />
            {item.name}
          </Link>
        </li>
      );
    });

  return (
    <div className="flex grow flex-col gap-y-2 overflow-y-auto bg-iron-900 px-3 pb-4 border-r border-iron-600">
      {/* Brand lockup — full Airspace Surveillance of New England logo on
          brand navy, with a Signal Red 'C2' badge for the platform tag.
          Header height bumped from h-14 to h-16 so the larger logo (h-12)
          has breathing room top and bottom. */}
      <div className="flex h-16 shrink-0 items-center px-2 border-b border-iron-700 mb-2">
        <BrandLockup />
      </div>

      {/* Navigation */}
      <nav className="flex flex-1 flex-col">
        <ul className="flex flex-1 flex-col gap-y-1">
          <li>
            <div className="section-label">Monitoring</div>
            <ul className="space-y-0.5">
              {renderNavItems(monitoringNav)}
            </ul>
          </li>

          <li className="mt-3">
            <div className="section-label">Management</div>
            <ul className="space-y-0.5">
              {renderNavItems(managementNav)}
            </ul>
          </li>

          {isAdmin && (
            <li className="mt-3">
              <div className="section-label">System</div>
              <ul className="space-y-0.5">
                {renderNavItems(adminNav)}
              </ul>
            </li>
          )}

          <li className="mt-auto pt-4 border-t border-iron-700">
            <Link
              to="/settings"
              onClick={onNavClick}
              className={clsx(
                'group flex items-center gap-x-3 rounded-sm px-3 py-2 text-[13px] font-medium transition-colors duration-150',
                location.pathname === '/settings'
                  ? 'bg-accent/10 text-accent nav-active'
                  : 'text-iron-300 hover:text-iron-100 hover:bg-iron-700'
              )}
            >
              <Cog6ToothIcon className={clsx('h-4 w-4 flex-shrink-0', location.pathname === '/settings' ? 'text-accent' : 'text-iron-400 group-hover:text-iron-200')} />
              Settings
            </Link>
          </li>

          <li className="pt-2">
            <div className="flex items-center gap-3 px-2 py-2">
              <UserInitials firstName={user?.firstName} lastName={user?.lastName} />
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-medium text-iron-100 truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-[10px] font-mono text-iron-400 truncate">{user?.email}</p>
              </div>
            </div>
          </li>
        </ul>
      </nav>
    </div>
  );
}

export default function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  // Open a single tenant-scoped WebSocket for the whole authenticated
  // session. Keeps dashboard timeline, live tracks, alerts, and sensor
  // banner in near-real-time sync without aggressive polling.
  useRealtimeEvents();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="h-screen flex overflow-hidden bg-iron-900">
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                      <XMarkIcon className="h-6 w-6 text-white" />
                    </button>
                  </div>
                </Transition.Child>
                <SidebarContent onNavClick={() => setSidebarOpen(false)} />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
        <SidebarContent />
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-12 shrink-0 items-center gap-x-4 border-b border-iron-600 bg-iron-900 px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-iron-400 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-5 w-5" />
          </button>

          <div className="h-5 w-px bg-iron-600 lg:hidden" />

          <div className="flex-1">
            <Breadcrumbs />
          </div>

          <div className="flex items-center gap-x-2">
            <NotificationDropdown />

            <div className="hidden lg:block h-5 w-px bg-iron-600 mx-1" />

            <Menu as="div" className="relative">
              <Menu.Button className="flex items-center gap-2 rounded-sm px-2 py-1.5 hover:bg-iron-700 transition-colors">
                <UserInitials firstName={user?.firstName} lastName={user?.lastName} />
                <span className="hidden lg:block text-[13px] text-iron-200 font-medium">
                  {user?.firstName}
                </span>
                <ChevronDownIcon className="hidden lg:block h-4 w-4 text-iron-400" />
              </Menu.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 mt-2 w-56 rounded-sm bg-iron-800 border border-iron-600 shadow-xl py-1 z-50 focus:outline-none">
                  <div className="px-3 py-2.5 border-b border-iron-700">
                    <p className="text-[13px] font-medium text-iron-100">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-[11px] font-mono text-iron-400 mt-0.5">{user?.email}</p>
                    <span className="inline-flex items-center mt-1.5 px-1.5 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-wider clip-bevel-sm border bg-accent/15 text-accent border-accent/30">
                      {user?.role}
                    </span>
                  </div>
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/settings"
                        className={clsx(
                          'flex items-center gap-2 px-3 py-2 text-[13px]',
                          active ? 'bg-iron-700 text-iron-100' : 'text-iron-300'
                        )}
                      >
                        <UserCircleIcon className="h-4 w-4" />
                        Profile & Settings
                      </Link>
                    )}
                  </Menu.Item>
                  <div className="border-t border-iron-700 mt-1">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={clsx(
                            'flex w-full items-center gap-2 px-3 py-2 text-[13px]',
                            active ? 'bg-iron-700 text-alert' : 'text-iron-400'
                          )}
                        >
                          <ArrowRightOnRectangleIcon className="h-4 w-4" />
                          Sign out
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>

        {/* Global system alert banner — renders nothing unless at least
            one sensor is offline/degraded, so normal operation stays quiet. */}
        <SystemAlertBanner />

        {/* Page content */}
        <main className="flex-1 overflow-auto min-h-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
